import React, { useState, useEffect } from 'react';
import { Portal, Dialog, TextInput, Button, Text, useTheme } from 'react-native-paper';
import { Platform, ScrollView } from 'react-native';
import { useTranslation } from 'react-i18next';
import { createCompanyVehicle } from '../api/apiService';
import FuelTypeSelector from './FuelTypeSelector';

interface Props {
  visible: boolean;
  onDismiss: () => void;
  onSuccess?: () => void;
}

export const AddVehicleModal: React.FC<Props> = ({ visible, onDismiss, onSuccess }) => {
  const { t } = useTranslation('manageVehicles');
  const theme = useTheme();
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [fuelType, setFuelType] = useState('gasolina');
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

  const formatRegistrationNumber = (input: string): string => {
    const cleanInput = input.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    const chars = cleanInput.slice(0, 6);
    if (chars.length <= 2) return chars;
    if (chars.length <= 4) return `${chars.slice(0, 2)}-${chars.slice(2)}`;
    return `${chars.slice(0, 2)}-${chars.slice(2, 4)}-${chars.slice(4)}`;
  };

  useEffect(() => {
    if (!visible) {
      // Reset form when modal closes
      setMake('');
      setModel('');
      setYear('');
      setLicensePlate('');
      setFuelType('gasolina');
      setIsFormValid(false);
      setErrors({});
      setLoading(false);
    }
  }, [visible]);

  const validateForm = () => {
    const licensePlateRegex = /^[A-Z0-9]{2}-[A-Z0-9]{2}-[A-Z0-9]{2}$/;
    const yearRegex = /^\d{4}$/;
    const currentYear = new Date().getFullYear();
    
    const isLicensePlateValid = licensePlateRegex.test(licensePlate);
    const isYearValid = yearRegex.test(year) && 
                       parseInt(year) >= 1900 && 
                       parseInt(year) <= currentYear;
    const isMakeValid = make.trim() !== '';
    const isModelValid = model.trim() !== '';

    return isLicensePlateValid && isYearValid && isMakeValid && isModelValid;
  };

  const handleLicensePlateChange = (text: string) => {
    const formatted = formatRegistrationNumber(text);
    setLicensePlate(formatted);
    if (errors.licensePlate) {
      setErrors({ ...errors, licensePlate: '' });
    }
  };

  const validateYear = (yearValue: string): string => {
    if (!yearValue || yearValue.length === 0) {
      return '';
    }
    const yearRegex = /^\d{4}$/;
    const currentYear = new Date().getFullYear();
    
    if (!yearRegex.test(yearValue)) {
      return t('yearInvalidFormat', 'Ano deve ter 4 dígitos');
    }
    
    const yearNum = parseInt(yearValue);
    if (yearNum < 1900) {
      return t('yearTooOld', 'Ano deve ser maior ou igual a 1900');
    }
    
    if (yearNum > currentYear) {
      return t('yearTooFuture', `Ano deve ser menor ou igual a ${currentYear}`);
    }
    
    return '';
  };

  const handleYearChange = (text: string) => {
    const cleaned = text.replace(/[^0-9]/g, '').slice(0, 4);
    setYear(cleaned);
    
    const yearError = validateYear(cleaned);
    setErrors(prev => ({ ...prev, year: yearError }));
  };

  useEffect(() => {
    const isValid = validateForm();
    setIsFormValid(isValid);
  }, [licensePlate, year, make, model, fuelType]);

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const payload: any = {
        registration_number: licensePlate.trim(),
        make: make.trim(),
        model: model.trim(),
        year: Number(year),
        fuel_type: fuelType,
      };

      const response = await createCompanyVehicle(
        payload.registration_number,
        payload.fuel_type,
        payload.make,
        payload.model,
        payload.year
      );

      if (response.ok) {
        onSuccess?.();
        onDismiss();
      } else {
        const errorData = response.data as unknown;
        if (isRecord(errorData)) {
          const newErrors: Record<string, string> = {};

          Object.entries(errorData).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              newErrors[key] = value.join(' ');
            } else if (typeof value === 'string') {
              newErrors[key] = value;
            } else if (value !== null && value !== undefined) {
              newErrors[key] = String(value);
            }
          });

          setErrors(newErrors);
        } else {
          setErrors({ general: t('createVehicleError', 'Failed to create vehicle. Please try again.') });
        }
      }
    } catch (err: any) {
      setErrors({ general: err.message || t('networkError', 'Network error. Please try again.') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Portal>
      <Dialog
        visible={visible}
        onDismiss={onDismiss}
        style={{ 
          maxHeight: '90%', 
          ...(Platform.OS === 'web' ? {
            maxWidth: 600,
            width: '90%',
            alignSelf: 'center',
          } : {
            width: '90%',
            alignSelf: 'center',
          })
        }}
      >
        <Dialog.Title>{t('addVehicle', 'Add Vehicle')}</Dialog.Title>
        <Dialog.ScrollArea>
          <ScrollView style={{ maxHeight: 500 }}>
            <Dialog.Content>
              <TextInput
                label={t('make', 'Marca')}
                value={make}
                onChangeText={setMake}
                style={{ marginBottom: 16 }}
                mode="outlined"
                disabled={loading}
              />
              
              <TextInput
                label={t('model', 'Modelo')}
                value={model}
                onChangeText={setModel}
                style={{ marginBottom: 16 }}
                mode="outlined"
                disabled={loading}
              />
              
              <TextInput
                label={t('year', 'Ano')}
                value={year}
                onChangeText={handleYearChange}
                style={{ marginBottom: 16 }}
                mode="outlined"
                keyboardType="numeric"
                maxLength={4}
                disabled={loading}
                error={!!errors.year}
              />
              {errors.year && (
                <Text style={{ fontSize: 12, marginTop: -12, marginBottom: 16, marginLeft: 12, color: theme.colors.error }}>
                  {errors.year}
                </Text>
              )}
              
              <TextInput
                label={t('licensePlate', 'Placa')}
                value={licensePlate}
                onChangeText={handleLicensePlateChange}
                style={{ marginBottom: 16 }}
                mode="outlined"
                autoCapitalize="characters"
                maxLength={8}
                disabled={loading}
                error={licensePlate.length > 0 && !/^[A-Z0-9]{2}-[A-Z0-9]{2}-[A-Z0-9]{2}$/.test(licensePlate)}
              />
              {licensePlate.length > 0 && !/^[A-Z0-9]{2}-[A-Z0-9]{2}-[A-Z0-9]{2}$/.test(licensePlate) && (
                <Text style={{ fontSize: 12, marginTop: -12, marginBottom: 16, marginLeft: 12, color: theme.colors.error }}>
                  {t('licensePlateError', 'Formato inválido. Use XX-XX-XX (letras e números)')}
                </Text>
              )}

              <Text style={{ marginBottom: 5 }}>{t('vehicle.fuelType', 'Fuel Type')}</Text>
              <FuelTypeSelector
                value={fuelType}
                onChange={setFuelType}
                style={{ marginBottom: 20 }}
              />

              {errors.general && (
                <Text style={{ color: theme.colors.error, marginBottom: 12 }}>{errors.general}</Text>
              )}
              {errors.registration_number && (
                <Text style={{ color: theme.colors.error, marginBottom: 12 }}>{errors.registration_number}</Text>
              )}
              {errors.make && (
                <Text style={{ color: theme.colors.error, marginBottom: 12 }}>{errors.make}</Text>
              )}
              {errors.model && (
                <Text style={{ color: theme.colors.error, marginBottom: 12 }}>{errors.model}</Text>
              )}
            </Dialog.Content>
          </ScrollView>
        </Dialog.ScrollArea>
        <Dialog.Actions>
          <Button onPress={onDismiss} disabled={loading}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            onPress={handleSubmit}
            loading={loading}
            disabled={loading || !isFormValid}
            mode="contained"
          >
            {t('add', 'Add')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
};

